import React, { useState } from "react";
import "./JobCard.css";
import { updateJobStatus } from "../../api/candidateApi";

function JobCard({ job, status, employeeId, candidateId }) {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [notes, setNotes] = useState("");
  const [localStatus, setLocalStatus] = useState(status?.status || "NOT_APPLIED");
  const [copied, setCopied] = useState(false);

  /* -------------------------------------------------
     COMPUTE "Indexed X minutes ago"
     Backend will soon add: job.dataJasIndexed
  -------------------------------------------------- */
  function formatIndexedTime() {
    if (!job.dataJasIndexed) return "Indexed time unavailable";

    const indexed = new Date(job.dataJasIndexed);
    const now = new Date();
    const diff = Math.floor((now - indexed) / 1000 / 60);

    if (diff < 1) return "Indexed just now";
    if (diff < 60) return `Indexed ${diff} minutes ago`;

    const hours = Math.floor(diff / 60);
    if (hours < 24) return `Indexed ${hours} hours ago`;

    const days = Math.floor(hours / 24);
    return `Indexed ${days} days ago`;
  }

  /* -------------------------------------------------
     STATUS COLOR STYLES
  -------------------------------------------------- */
  const statusColor = {
    NOT_APPLIED: "border-blue",
    APPLIED: "border-green",
    NOT_FIT: "border-red",
    UNABLE_TO_APPLY: "border-red"
  }[localStatus];


  /* -------------------------------------------------
     Placeholder for API (no call yet)
  -------------------------------------------------- */
  async function updateStatus(newStatus, note = null) {
    try {
      const payload = {
        candidateId,
        employeeId,
        jobId: job.id,
        status: newStatus,
        reason: note
      };

      const res = await updateJobStatus(payload);

      // 🔥 UPDATE CARD INSTANTLY
      setLocalStatus(newStatus);

    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update job status");
    }
  }



  function handleStatusClick(newStatus) {
    if (["NOT_FIT", "UNABLE_TO_APPLY"].includes(newStatus)) {
      setPendingStatus(newStatus);
      setShowNotesModal(true);
      return;
    }

    updateStatus(newStatus);
  }

  function submitNotes() {
    updateStatus(pendingStatus, notes);
    setShowNotesModal(false);
    setNotes("");
  }

const isCopyAvailable = job.copyjd && job.copyjd.length > 10;

async function copyToClipboard(text) {
  try {
    // Modern browsers
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.warn("Clipboard API failed, using fallback", err);

    try {
      // iOS fallback
      const textarea = document.createElement("textarea");
      textarea.value = text;

      // Prevent scrolling (important for iOS)
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);

      return successful;
    } catch (fallbackErr) {
      console.error("Fallback copy failed", fallbackErr);
      return false;
    }
  }
}

async function handleCopyJD() {
  if (!isCopyAvailable) return;

  const success = await copyToClipboard(job.copyjd);

  if (success) {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  } else {
    alert("Copy failed. Please copy manually.");
  }
}

  /* -------------------------------------------------
     RENDER
  -------------------------------------------------- */
  return (
    <>
      <div className={`jobcard ${statusColor}`}>


        {/* LEFT CONTENT */}
        <div className="jobcard-left">

          <div className={`job-status-badge-top status-${localStatus}`}>
            {localStatus.replace(/_/g, " ")}
          </div>

          <div className="indexed-time">{formatIndexedTime()}</div>

          <h3 className="job-title">{job.title}</h3>
          <div className="job-company">{job.company}</div>

          <div className="job-meta">
            {job.location && <span>{job.location}</span>}
            {job.employmentType && <span>{job.employmentType}</span>}
          </div>

          <div className="job-attributes">
            {job.securityClearanceRequired !== null && (
              <div>
                <strong>Security Clearance:</strong>{" "}
                {job.securityClearanceRequired ? "Required" : "Not Required"}
              </div>
            )}

            {job.sponsorshipMentioned && (
              <div>
                <strong>Sponsorship:</strong> {job.sponsorshipMentioned}
              </div>
            )}

            {job.minimumYearsRequired !== null && (
              <div>
                <strong>Min Experience:</strong> {job.minimumYearsRequired} yrs
              </div>
            )}
          </div>

          <div className="job-actions">
            <a
              href={job.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-open"
            >
              Apply Now
            </a>

            {localStatus === "APPLIED" ? (
              <button className="btn-blue" onClick={() => handleStatusClick("NOT_APPLIED")}>
                Mark Not Applied
              </button>
            ) : (
              <button className="btn-green" onClick={() => handleStatusClick("APPLIED")}>
                Mark Applied
              </button>
            )}

            <button className="btn-red" onClick={() => handleStatusClick("NOT_FIT")}>
              Not Fit
            </button>

            <button className="btn-red" onClick={() => handleStatusClick("UNABLE_TO_APPLY")}>
              Unable
            </button>

            <button
              className={`btn-copyjd ${isCopyAvailable ? "enabled" : "disabled"} ${copied ? "copied" : ""}`}
              onClick={handleCopyJD}
              disabled={!isCopyAvailable}
            >
              {!isCopyAvailable ? "JD Not Available" : copied ?(
                  <>
                    <span className="tick">✓</span> Copied
                  </>
                ) : (
                  "Copy JD"
                )}
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="jobcard-right">

          <div className="right-info">
            <div className="ri-badge">Posted:
              {job.datePosted ? new Date(job.datePosted).toLocaleDateString() : "N/A"}
            </div>

            <div className="ri-badge">Source: {job.source || "N/A"}</div>
          </div>


          <div className="score-box">
            <div className="score-number">
              {status?.matchScore ? `${status.matchScore}%` : "—"}
            </div>
            <div className="score-label">Match Score</div>
          </div>

        </div>
      </div> {/* END jobcard */}

      {/* NOTES MODAL — OUTSIDE main card DIV */}
      {showNotesModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Reason Required</h3>

            <textarea
              placeholder="Example: Client needs GoLang experience"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <button className="modal-submit" disabled={!notes.trim()} onClick={submitNotes}>
              Submit
            </button>

            <button className="modal-cancel" onClick={() => setShowNotesModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );

}

export default JobCard;
